import { memo, useRef } from 'react';

import { MemoizedMarkdownBlock } from '@/components/ui/smoothdown/MemoizedMarkdownBlock';
import { classList } from '@/utils/cn';

import { EditorTableControls } from './components/editor-table-controls';

interface EditorPreviewProps {
  content: string;
  className?: string;
  dir?: 'ltr' | 'rtl';
  editable?: boolean;
  onBeforeTableExpand?: () => void;
  onUpdate?: (markdown: string) => void;
}

export const EditorPreview = memo(function EditorPreview({
  content,
  className,
  dir = 'ltr',
  editable = false,
  onBeforeTableExpand,
  onUpdate,
}: EditorPreviewProps) {
  const rootRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={rootRef}
      className={classList(
        'copyable-block-preview lb-editor lb-editor-table-root smoothdown',
        className
      )}
      dir={dir}
    >
      <div className="copyable-block-preview-content tiptap ProseMirror markdown prose prose-bird prose-base select-text cursor-text break-words focus:outline-none">
        <MemoizedMarkdownBlock content={content} type="copyable" softBreaks />
        <p aria-hidden="true" className="trailing-empty-line">
          <br />
        </p>
      </div>
      <EditorTableControls
        editable={editable && Boolean(onUpdate)}
        markdown={content}
        onBeforeExpand={onBeforeTableExpand}
        onChange={onUpdate}
        rootRef={rootRef}
      />
    </div>
  );
}, hasSamePreviewProps);

function hasSamePreviewProps(
  prevProps: EditorPreviewProps,
  nextProps: EditorPreviewProps
) {
  return (
    prevProps.content === nextProps.content &&
    prevProps.className === nextProps.className &&
    prevProps.dir === nextProps.dir &&
    prevProps.editable === nextProps.editable &&
    prevProps.onBeforeTableExpand === nextProps.onBeforeTableExpand &&
    prevProps.onUpdate === nextProps.onUpdate
  );
}
